
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { getBrandingColorPalette } from '../../../base/styles';

// Design System Constants - Aligned with main styles
const SPACING = {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32
};

const COLORS = {
    backdrop: 'rgba(0, 0, 0, 0.54)',
    bgPrimary: '#ffffff',
    bgHeader: '#4a6790',
    border: '#e5eef9',
    borderHeader: '#d1e3f6',
    textPrimary: '#4b6790',
    textSecondary: '#5d769b',
    textTertiary: '#6c7a8f',
    hover: '#f8fbff',
    primary: '#4b6790'
};

const SHADOWS = {
    dialog: '0px 8px 32px rgba(75, 103, 144, 0.16), 0px 2px 8px rgba(75, 103, 144, 0.08)'
};

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const slideUp = keyframes`
    0% {
        transform: translateY(20px);
        opacity: 0;
    }
    100% {
        transform: translateY(0);
        opacity: 1;
    }
`;

const useStyles = makeStyles()(() => {
    return {
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: COLORS.backdrop,
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 60,
            paddingBottom: 40,
            alignItems: 'center',
            paddingLeft: SPACING.md,
            paddingRight: SPACING.md,
            animation: `${fadeIn} 0.2s ease-out`,
            overflowY: 'auto',
            overflowX: 'hidden',

            '@media (max-width: 1024px)': {
                paddingTop: 40,
                paddingBottom: 30
            },

            '@media (max-width: 900px)': {
                paddingTop: 40,
                paddingBottom: 20,
                paddingLeft: SPACING.sm,
                paddingRight: SPACING.sm
            },

            '@media (max-width: 640px)': {
                paddingTop: 20,
                paddingBottom: 15,
                paddingLeft: SPACING.xs,
                paddingRight: SPACING.xs
            },

            '@media (max-width: 480px)': {
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
                alignItems: 'stretch'
            }
        },

        dialog: {
            backgroundColor: COLORS.bgPrimary,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 20,
            boxShadow: SHADOWS.dialog,
            width: '100%',
            maxWidth: 1040,
            maxHeight: 'calc(100vh - 100px)',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            animation: `${slideUp} 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)`,
            position: 'relative',
            zIndex: 10001,
            overflow: 'hidden',

            '@media (min-width: 1200px)': {
                maxHeight: 530
            },

            '@media (min-width: 1200px) and (min-height: 800px)': {
                maxHeight: 530
            },

            '@media (max-width: 1024px)': {
                maxHeight: 'calc(100vh - 80px)'
            },

            '@media (max-width: 900px)': {
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 40px)',
                borderRadius: 16
            },

            '@media (max-width: 640px)': {
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 30px)',
                borderRadius: 14,
                marginTop: 15
            },

            '@media (max-width: 480px)': {
                maxWidth: '100%',
                maxHeight: '100vh',
                borderRadius: 0,
                marginTop: 0,
                marginBottom: 0
            }
        },

        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${SPACING.md}px ${SPACING.xl}px`,
            borderBottom: `1px solid ${COLORS.borderHeader}`,
            flexShrink: 0,
            minHeight: 48,
            background: getBrandingColorPalette(interfaceConfig)?.primary,

            '@media (max-width: 640px)': {
                padding: `${SPACING.sm}px ${SPACING.md}px`,
                minHeight: 44
            },

            '@media (max-width: 480px)': {
                padding: `${SPACING.sm}px ${SPACING.sm}px`,
                minHeight: 40
            }
        },

        title: {
            fontSize: 18,
            fontWeight: 600,
            color: interfaceConfig?.applyMeetingSettings ? `${getBrandingColorPalette(interfaceConfig)?.accent1} !important` : '#fff !important',
            margin: 0,
            padding: 0,
            letterSpacing: '-0.02em',
            lineHeight: 1.3,

            '@media (max-width: 640px)': {
                fontSize: 16
            },

            '@media (max-width: 480px)': {
                fontSize: 15
            }
        },

        closeButton: {
            background: COLORS.bgPrimary,
            border: `1px solid ${COLORS.border}`,
            cursor: 'pointer',
            padding: SPACING.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.textSecondary,
            borderRadius: 8,
            transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
            minWidth: 32,
            minHeight: 32,

            '&:hover': {
                backgroundColor: COLORS.hover,
                color: COLORS.textPrimary,
                transform: 'scale(1.05)'
            },

            '&:active': {
                transform: 'scale(0.95)',
                backgroundColor: COLORS.hover
            },

            '&:focus-visible': {
                outline: `2px solid ${COLORS.textPrimary}`,
                outlineOffset: 2,
                backgroundColor: COLORS.hover
            },

            '& svg': {
                width: 20,
                height: 20,
                transition: 'transform 180ms cubic-bezier(0.4, 0, 0.2, 1)'
            },

            '&:hover svg': {
                transform: 'rotate(90deg)'
            },

            '@media (max-width: 640px)': {
                minWidth: 40,
                minHeight: 40,
                padding: SPACING.md
            },

            '@media (max-width: 480px)': {
                minWidth: 44,
                minHeight: 44
            }
        },

        content: {
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }
    };
});

/**
 * Custom Dialog Component
 * A self-contained dialog with backdrop and animations
 * Note: Redux dialog system controls mounting/unmounting, so isOpen is not needed
 */
function CustomDialog({ children, onClose, t: tProp, title, titleKey }) {
    const { classes } = useStyles();
    const { t: tHook } = useTranslation();
    const t = tProp || tHook;
    const dialogRef = useRef(null);
    const backdropRef = useRef(null);


    // Handle escape key - Always active when dialog is mounted
    useEffect(() => {
        const handleEscape = e => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [ onClose ]);

    // Handle backdrop click
    const handleBackdropClick = e => {
        // Only close if clicking directly on the backdrop, not on the dialog content
        if (e.target === backdropRef.current || e.target === e.currentTarget) {
            onClose();
        }
    };

    // Focus trap - Always active when dialog is mounted
    useEffect(() => {
        if (!dialogRef.current) {
            return;
        }

        const focusableElements = dialogRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        const handleTab = e => {
            if (e.key !== 'Tab') {
                return;
            }

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    if (lastElement) {
                        lastElement.focus();
                    }
                }
            } else if (document.activeElement === lastElement) {
                e.preventDefault();
                if (firstElement) {
                    firstElement.focus();
                }
            }
        };

        document.addEventListener('keydown', handleTab);
        firstElement?.focus();

        return () => {
            document.removeEventListener('keydown', handleTab);
        };
    }, []);

    // Always render when component is mounted (Redux dialog system controls mounting)
    const displayTitle = title || (titleKey && t ? t(titleKey) : '') || 'Dialog';

    return (
        <div
            className = { classes.backdrop }
            onClick = { handleBackdropClick }
            ref = { backdropRef }>
            <div
                aria-labelledby = 'custom-dialog-title'
                aria-modal = { true }
                className = { classes.dialog }
                onClick = { e => {
                    // Prevent clicks inside dialog from closing it
                    e.stopPropagation();
                } }
                ref = { dialogRef }
                role = 'dialog'>
                <div className = { classes.header }>
                    <h2
                        className = { classes.title }
                        id = 'custom-dialog-title'>
                        {displayTitle}
                    </h2>
                    <button
                        aria-label = { t ? t('dialog.accessibilityLabel.close', { defaultValue: 'Close' }) : 'Close' }
                        className = { classes.closeButton }
                        onClick = { onClose }
                        type = 'button'>
                        <svg
                            fill = 'none'
                            height = '24'
                            viewBox = '0 0 24 24'
                            width = '24'
                            xmlns = 'http://www.w3.org/2000/svg'>
                            <path
                                d = 'M18 6L6 18M6 6l12 12'
                                stroke = 'currentColor'
                                strokeLinecap = 'round'
                                strokeLinejoin = 'round'
                                strokeWidth = '2' />
                        </svg>
                    </button>
                </div>
                <div className = { classes.content }>
                    {children}
                </div>
            </div>
        </div>
    );
}

export default CustomDialog;

